import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import QrCodeImage, { type QrCodeState } from 'app/javascript/components/shared/QrCodeImage';
import PrintablePosterFooter from 'app/javascript/components/shared/PrintablePosterFooter';
import PrintablePosterLayout from 'app/javascript/components/shared/PrintablePosterLayout';
import StoreBadges from 'app/javascript/components/shared/StoreBadges';
import ExpiredQrCodePoster from 'app/javascript/components/qr_onboarding/ExpiredQrCodePoster';
import PosterSchedules from 'app/javascript/components/qr_onboarding/PosterSchedules';
import { usePublicCompany } from 'app/javascript/queries/useCompanies';

const ONBOARDING_STEPS = [
  {
    title: 'Aponte a câmera',
    description: 'Escaneie este QR Code com a câmera do celular.',
  },
  {
    title: 'Baixe o BlackBelts',
    description: 'Se ainda não tiver o app, instale pela App Store ou Google Play.',
  },
  {
    title: 'Entre na equipe',
    description: 'Depois de instalar, volte a este cartaz e escaneie novamente para concluir seu cadastro.',
  },
] as const;

/**
 * Printable A4 poster served by appending `.pdf` to the canonical join URL. It
 * renders outside the settings layout so the browser prints only the sheet.
 */
export default function CompanyQrPrint() {
  const { slug, token } = useParams({ strict: false }) as { slug: string; token: string };
  const { data: company, isLoading: companyLoading, isError: companyError } = usePublicCompany(slug);
  const [qrCodeState, setQrCodeState] = useState<QrCodeState>('loading');
  const qrCodeExpired = document.getElementById('root')?.dataset.onboardingQrCodeExpired === 'true';

  if (companyLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="h-7 w-7 animate-spin text-amber-500" />
      </div>
    );
  }

  if (companyError || !company) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-8 text-center">
        <p className="text-slate-600">Não foi possível carregar as informações desta equipe.</p>
      </div>
    );
  }

  if (qrCodeExpired) {
    return <ExpiredQrCodePoster companyName={company.name} logoUrl={company.logo_url} />;
  }

  const joinUrl = new URL(`/join/${encodeURIComponent(slug)}/${encodeURIComponent(token.replace(/\.pdf$/, ''))}`, window.location.origin);

  return (
    <PrintablePosterLayout qrCodeState={qrCodeState}>
      <header className="flex flex-col items-start gap-4 border-b-4 border-yellow-400 pb-5 sm:flex-row sm:items-center sm:gap-5 print:flex-row print:items-center print:gap-5">
        {company.logo_url && <img src={company.logo_url} alt={company.name} className="h-20 w-20 shrink-0 rounded-xl border border-slate-200 object-cover" />}
        <div className="min-w-0">
          <p className="mb-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Cadastro pelo app</p>
          <h1 className="break-words font-bebas text-5xl leading-none tracking-wide text-slate-950 sm:text-6xl print:text-5xl">{company.name}</h1>
          {company.description && <p className="mt-2 line-clamp-2 max-w-2xl text-sm leading-relaxed text-slate-600">{company.description}</p>}
        </div>
      </header>

      <main className="flex flex-1 flex-col gap-6 py-5 print:gap-4 print:py-4">
        <section className="break-inside-avoid rounded-2xl border-2 border-yellow-400 bg-white p-6 text-slate-950 print:p-5">
          <h2 className="text-balance font-bebas text-4xl leading-none tracking-wide text-slate-950 sm:text-5xl print:text-4xl">
            Entre para a equipe pelo BlackBelts
          </h2>

          <div className="mt-5 grid items-center gap-7 sm:grid-cols-[1fr_auto] print:mt-4 print:grid-cols-[minmax(0,1fr)_auto] print:gap-5">
            <div className="min-w-0">
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 print:mt-2">
                O cadastro de novos alunos acontece no aplicativo. Este QR Code abre o BlackBelts ou mostra onde baixar o app.
              </p>

              <ol className="mt-5 space-y-3 print:mt-4 print:space-y-2">
                {ONBOARDING_STEPS.map((step, index) => (
                  <li key={step.title} className="flex items-start gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-sm font-black text-yellow-950">
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-950">{step.title}</p>
                      <p className="text-xs leading-relaxed text-slate-600">{step.description}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <StoreBadges size="sm" className="mt-5 print:mt-4" />
            </div>

            <div className="mx-auto rounded-2xl border border-slate-200 bg-white p-3 sm:mx-0">
              <QrCodeImage value={joinUrl.toString()} size={236} className="h-auto max-w-full" onStateChange={setQrCodeState} />
            </div>
          </div>
          {qrCodeState === 'error' && (
            <p role="alert" className="mt-4 text-center text-sm font-medium text-red-700 print:hidden">
              Não foi possível gerar o QR Code. Recarregue a página antes de imprimir.
            </p>
          )}
        </section>

        <PosterSchedules schedules={company.class_schedules} />
      </main>

      <PrintablePosterFooter company={company} />
    </PrintablePosterLayout>
  );
}
